'use client';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/core/utils/cn';
import { Search, ArrowRight, CornerDownLeft, ArrowUp, ArrowDown, X } from 'lucide-react';
import type { MenuItem } from '@/core/types/common.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

interface CommandRoute {
  label: string;
  href: string;
  section?: string;
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function flattenMenuItems(items: MenuItem[], parentLabel?: string): CommandRoute[] {
  const routes: CommandRoute[] = [];
  for (const item of items) {
    if (item.href) {
      routes.push({
        label: parentLabel ? `${parentLabel} > ${item.label}` : item.label,
        href: item.href,
        section: item.section,
      });
    }
    if (item.children) {
      for (const child of item.children) {
        if (child.href) {
          routes.push({
            label: `${item.label} > ${child.label}`,
            href: child.href,
            section: item.section,
          });
        }
      }
    }
  }
  return routes;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * The menu of the portal the palette is opened from. Only its pages are
   * offered: a palette listing every portal's routes sent hotel staff to
   * /admin pages that answer 403, and super admins to PMS pages with no hotel.
   */
  items: MenuItem[];
}

export function CommandPalette({ open, onClose, items }: CommandPaletteProps) {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useTranslation();
  const routes = useMemo(() => flattenMenuItems(items), [items]);

  const results = useMemo(() => {
    if (!query.trim()) return routes.slice(0, 12);
    const lower = query.toLowerCase();
    return routes.filter(
      (r) => r.label.toLowerCase().includes(lower) || r.href.toLowerCase().includes(lower)
    ).slice(0, 12);
  }, [query, routes]);

  /* Reset state when opening */
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset local UI state on open
      setQuery('');
      setActiveIndex(0);
      // Focus the input after mount via rAF
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  /* Global Cmd/Ctrl+K handler */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) { onClose(); } else { /* parent should open via store */ }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  /* Keyboard navigation inside the palette */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % results.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
        return;
      }
      if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        const target = results[activeIndex];
        if (target) {
          router.push(target.href);
          onClose();
        }
      }
    },
    [results, activeIndex, router, onClose]
  );

  /* Scroll active item into view */
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.children[activeIndex] as HTMLElement | undefined;
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  /* Keep activeIndex in range when results change */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset local UI state on query change
    setActiveIndex(0);
  }, [query]);

  const handleSelect = useCallback(
    (route: CommandRoute) => {
      router.push(route.href);
      onClose();
    },
    [router, onClose]
  );

  if (!open) return null;

  return (
    <div id="cmd-palette-overlay" className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onMouseDown={onClose}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        id="cmd-palette-panel"
        className="relative z-10 w-full max-w-lg rounded-2xl border border-hms-border bg-hms-card shadow-2xl shadow-black/40 overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-hms-border px-5 py-4">
          <Search id="cmd-palette-search-icon" className="w-5 h-5 text-hms-textDim shrink-0" />
          <input
            id="cmd-palette-input"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('shared.commandPalette.searchPlaceholder')}
            className="flex-1 bg-transparent text-sm text-hms-text placeholder:text-hms-textDim outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <button id="cmd-palette-close" type="button" onClick={onClose} className="p-1 rounded-md text-hms-textDim hover:text-hms-text hover:bg-hms-surface transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results list */}
        <div id="cmd-palette-results" ref={listRef} className="max-h-[320px] overflow-y-auto py-2">
          {results.length === 0 && (
            <div id="cmd-palette-empty" className="px-5 py-8 text-center">
              <p className="text-sm text-hms-textDim">No results found for &quot;{query}&quot;</p>
            </div>
          )}
          {results.map((route, i) => (
            <button
              key={route.href}
              id={`cmd-palette-item-${i}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(route); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={cn(
                'flex items-center gap-3 w-full px-5 py-3 text-left transition-colors',
                i === activeIndex ? 'bg-hms-gold/10 text-hms-gold' : 'text-hms-text hover:bg-hms-surface'
              )}
            >
              <ArrowRight className={cn('w-4 h-4 shrink-0', i === activeIndex ? 'text-hms-gold' : 'text-hms-textDim')} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{route.label}</p>
                <p className={cn('text-[10px] truncate', i === activeIndex ? 'text-hms-gold/70' : 'text-hms-textDim')}>{route.href}</p>
              </div>
              {route.section && (
                <span className={cn('text-[9px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-md shrink-0', i === activeIndex ? 'bg-hms-gold/15 text-hms-gold' : 'bg-hms-surface text-hms-textDim')}>
                  {route.section}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Footer hints */}
        <div id="cmd-palette-footer" className="flex items-center gap-4 border-t border-hms-border px-5 py-3">
          <span className="flex items-center gap-1.5 text-[10px] text-hms-textDim">
            <span className="flex items-center gap-0.5 rounded border border-hms-border bg-hms-surface px-1.5 py-0.5 text-[9px] font-medium text-hms-textSub"><ArrowUp className="w-2.5 h-2.5" /><ArrowDown className="w-2.5 h-2.5" /></span>
            Navigate
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-hms-textDim">
            <span className="flex items-center rounded border border-hms-border bg-hms-surface px-1.5 py-0.5 text-[9px] font-medium text-hms-textSub"><CornerDownLeft className="w-2.5 h-2.5" /></span>
            Open
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-hms-textDim">
            <span className="rounded border border-hms-border bg-hms-surface px-1.5 py-0.5 text-[9px] font-medium text-hms-textSub">Esc</span>
            Close
          </span>
        </div>
      </div>
    </div>
  );
}
